import { useMutation } from "@tanstack/react-query";
import React from "react";
import {
  LoginApi,
  RegisterApi,
  getAuthURLApi,
  linkTicketmasterAccountApi,
} from "../api/auth";
import { useTeamConfig } from "./useTeamConfig";
import { useRouter } from "next/router";
import { routes } from "../routes";
import { RegisterLocal } from "@/types";
import { userState } from "../store/data/user";
import { useDispatch } from "react-redux";
import { sessionLocalStorageKey } from "../config/constants";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { parseApiErrorMessage, parseErrorMessage } from "../utils/utils";
import { createBase64String } from "../utils/auth.utils";
import { globalConfig } from "../config/global_config";
import { OAuthState } from "../store/data/types";

export type AuthCallback<T = any> = OAuthState;

/**
 * @param loginCallback callback function runs after login api success,
 * decoded state will be available as parameter
 * @returns void
 */
export function useTicketmaster(
  loginCallback?: (stateDecoded: any, response: any) => void
) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { teamSchema } = useTeamConfig();
  const { frontendUrl } = globalConfig;
  const [isLoading, setIsLoading] = React.useState(false);
  const code = router.query.code;
  const state = router.query.state as string;
  const redirectUri = `${frontendUrl}/tmcallback/${teamSchema}/${routes.oauthNoQuery}`;
  const getAuthUrl = useMutation(getAuthURLApi);
  const linkTmAccount = useMutation(LoginApi());

  const loginErrorModal = useToggle();
  const [loginErrorMessage, setLoginErrorMessage] = React.useState("");

  /**
   * Function to initiate tm auth request
   * @param state any valid javascript value
   */
  const handleConnectTm = async (state: any) => {
    try {
      setIsLoading(true);
      await getAuthUrl.mutateAsync(teamSchema, {
        onSuccess: ({ data }) => {
          state = window.btoa(JSON.stringify(state)).replaceAll("=", "");
          let authURL = data.authURL
            .replace("REDIRECT_URL", redirectUri)
            .replace("STATE", state);
          window.location.replace(authURL);
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // linking tm account through api call
  const handleLinkAccount = async () => {
    try {
      setIsLoading(true);
      let payload = {
        code: code as string,
        redirectUri,
        teamSchema,
      };
      await linkTmAccount.mutateAsync(payload, {
        onSuccess: ({ data }) => {
          let stateDecoded = JSON.parse(window.atob(state));
          if (loginCallback) {
            loginCallback({ ...stateDecoded, code }, data);
          }
        },
        onError: (error) => {
          let errorMessage = parseApiErrorMessage(error);
          setLoginErrorMessage(errorMessage);
          loginErrorModal.onOpen();
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // React.useEffect(() => {
  //   if (!!code && !!state) {
  //     handleLinkAccount();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [code, state]);

  return {
    isLoading,
    linkTmAccount,
    handleConnectTm,
    loginErrorMessage,
    loginErrorModal,
    handleLinkAccount,
  };
}
